import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import EmergencySOS from '../components/EmergencySOS';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';
import { 
  Car, 
  Home, 
  MapPin, 
  Calendar, 
  CreditCard, 
  Gift, 
  History,
  User, 
  LogOut,
  Menu,
  X,
  AlertTriangle
} from 'lucide-react';

const RiderLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sosOpen, setSosOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/rider', icon: Home, label: 'Dashboard', exact: true },
    { path: '/rider/book', icon: MapPin, label: 'Book Ride' },
    { path: '/rider/schedule', icon: Calendar, label: 'Schedule' },
    { path: '/rider/history', icon: History, label: 'Ride History' },
    { path: '/rider/payments', icon: CreditCard, label: 'Payments' },
    { path: '/rider/loyalty', icon: Gift, label: 'Rewards' },
    { path: '/rider/profile', icon: User, label: 'Profile' },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="text-white">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-bold text-white">REDDI</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Link to="/rider/book">
              <Button size="sm" className="bg-red-600 hover:bg-red-700 rounded-full px-4">
                Book
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-slate-900 z-50 transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <Link to="/rider" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-display font-bold text-white">REDDI</span>
              </Link>
              <div className="hidden lg:flex items-center gap-2">
                <LanguageSelector />
                <NotificationBell />
              </div>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Book Button */}
          <div className="p-4 border-b border-slate-800">
            <Link to="/rider/book" onClick={() => setSidebarOpen(false)}>
              <Button className="w-full rounded-full bg-red-600 hover:bg-red-700 font-bold">
                <MapPin className="w-4 h-4 mr-2" />
                Book a Ride
              </Button>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive(item.path, item.exact)
                    ? 'bg-red-600/20 text-white border-l-4 border-red-600'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* SOS Button */}
          <div className="p-4 border-t border-slate-800">
            <Button
              onClick={() => setSosOpen(true)}
              variant="outline"
              className="w-full border-red-600 text-red-500 hover:bg-red-600/20 font-semibold"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Emergency SOS
            </Button>
          </div>

          {/* User Info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-300" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">{user?.full_name || 'Rider'}</p>
                <p className="text-xs text-slate-400">Rider</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <Outlet />
      </main>

      {/* Emergency SOS Modal */}
      <EmergencySOS isOpen={sosOpen} onClose={() => setSosOpen(false)} />
    </div>
  );
};

export default RiderLayout;
